import { TextInput } from "@circleco/compass/components/TextInput";
import { useEffect, useMemo, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";

import { useEmojiPickerV4Context } from "../../EmojiPickerV4Context";
import type { CustomEmojiShape } from "../../types";

interface ShapePickerProps {
  dismiss: () => void;
}

export const ShapePicker = ({ dismiss }: ShapePickerProps) => {
  const [searchValue, setSearchValue] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const {
    customShapeIcons = [],
    onEmojiSelect,
    name,
    shouldCloseOnSelect,
  } = useEmojiPickerV4Context();

  const groupedShapes = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    return customShapeIcons
      .map(group => ({
        id: group.id,
        name: group.name,
        shapes: query
          ? group.emojis.filter(s => s.name.toLowerCase().includes(query))
          : group.emojis,
      }))
      .filter(g => g.shapes.length > 0);
  }, [customShapeIcons, searchValue]);

  const handleShapeClick = (shape: CustomEmojiShape) => {
    onEmojiSelect?.({ identifier: shape.id, emoji_type: "shapes" }, name);
    if (shouldCloseOnSelect) dismiss();
  };

  return (
    <div className="flex flex-col gap-3 pt-3">
      <TextInput
        ref={searchInputRef}
        icon="magnifying-glass"
        placeholder={t("emoji_picker_v4.search_shapes_placeholder")}
        value={searchValue}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearchValue(e.target.value)
        }
      />
      <div className="h-[320px] overflow-y-auto">
        {groupedShapes.map(group => (
          <div key={group.id}>
            <p className="text-light px-1 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.35px]">
              {group.name}
            </p>
            <div
              className="grid gap-0"
              style={{
                gridTemplateColumns: "repeat(auto-fill, 34px)",
                justifyContent: "space-between",
              }}
            >
              {group.shapes.map(shape => (
                <button
                  key={shape.id}
                  type="button"
                  className="hover:bg-tertiary flex size-[34px] items-center justify-center rounded-lg"
                  onClick={() => handleShapeClick(shape)}
                  title={shape.name}
                >
                  <img
                    loading="eager"
                    src={shape.skins[0]?.src}
                    alt={shape.name}
                    className="size-6 object-contain"
                  />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
